import { fakeCryptoRandom } from './fake-random'
import { sampleAlphabetChars } from './sample'
import { cryptoRandom, type Random } from './types'

export const uid = makeUID(cryptoRandom)
const incrementalBlockIdAlphabet =
  'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789_'

// length: 2 - 8 ids ~ 1% of collision
// length: 4 - 562 ids ~ 1% of collision
// length: 6 - 35k ids ~ 1% of collision
// length: 12 - 8B ids ~ 1% of collision
// length: 14 - 558B ids ~ 1% of collision
// length: 16 - 35T ids ~ 1% of collision

// length: 15 (digits only) - 4M ids ~ 1% of collision

function makeUID(random: Random) {
  return (size = 21, existingIds?: string[]) => {
    let id = getId(random, size)
    if (!existingIds?.length) return id
    while (existingIds.includes(id)) id = getId(random, size)
    return id
  }
}

function getId(random: Random, size: number) {
  // Rejection sampling (not modulo/bitmask) because ALPHABET.length (63) is
  // not a power of 2 and doesn't evenly divide 256; see sample.ts for why.
  return sampleAlphabetChars(fakeCryptoRandom ?? random, size)
}

export function uidNumber(length: number): string {
  if (length < 1 || length > 16) {
    throw new Error('Length must be between 1 and 16')
  }

  const min = 10 ** (length - 1)
  const max = 10 ** length - 1

  const bytes = (fakeCryptoRandom ?? cryptoRandom).getRandomValues(
    new Uint8Array(1),
  )
  const scaled = Math.floor(
    (bytes[0] / (0xffffffff + 1)) * (max - min + 1) + min,
  )
  return scaled.toString()
}

export function incrementalBlockId(): () => string {
  let index = 0

  return () => {
    const id = incrementalIdAt(index)
    index += 1
    return id
  }
}

function incrementalIdAt(index: number): string {
  const base = incrementalBlockIdAlphabet.length
  let length = 1
  let countForLength = base
  let offset = index

  while (offset >= countForLength) {
    offset -= countForLength
    length += 1
    countForLength *= base
  }

  let id = ''
  for (let position = 0; position < length; position++) {
    const divisor = base ** (length - position - 1)
    const digit = Math.floor(offset / divisor) % base
    id += incrementalBlockIdAlphabet[digit]
  }
  return id
}
